import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import {
  Bot, Loader2, Plug, Unplug, RefreshCw, AlertTriangle, Stethoscope,
  Sparkles, Clock, QrCode, Workflow, FlaskConical, Check, X, ChevronUp, ChevronDown,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  useZaloBotConfigStatus,
  useZaloBotAccounts,
  useConnectZaloBot,
  useRebindZaloBotWebhook,
  useDeleteZaloAccount,
  useDiagnoseZaloBot,
  type ZaloBotDiagnosis,
} from '@/hooks/use-integrations'
import {
  SelectableTable,
  accountColumns,
  accountSearchText,
  ACCOUNT_FILTERS,
  BulkDisconnectButton,
} from './accounts-table'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { useMyPermissions } from '@/hooks/use-settings'
import { noConnectHint } from './channel-catalog'
import { useQuotaExceededDialog } from '@/components/billing/quota-exceeded-dialog'

/** Ô dán Bot Token — toàn bộ việc đấu nối gói gọn ở đây. */
function ConnectForm({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation('chanZalo')
  const [token, setToken] = useState('')
  const connect = useConnectZaloBot()
  const onboardingReturn = useOnboardingReturn()
  const quotaDialog = useQuotaExceededDialog()

  const handleConnect = () => {
    const tk = token.trim()
    if (!tk) {
      toast.error(t('zaloBot.connect.tokenRequired'))
      return
    }
    connect.mutate(tk, {
      onSuccess: (r) => {
        setToken('')
        if (r.webhookOk) {
          toast.success(t('zaloBot.connect.success', { name: r.name || 'Zalo Bot' }))
          onboardingReturn.complete(1)
        }
        // Bot đã lưu nhưng Zalo chưa gọi thử được: nói rõ lý do thay vì báo thành công chung chung.
        else toast.warning(r.message || t('zaloBot.connect.webhookFailed'))
      },
      onError: (e) => { if (!quotaDialog.handle(e)) toast.error(apiError(e)) },
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Plug className="h-4 w-4" />
          {t('zaloBot.connect.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            <Trans
              t={t}
              i18nKey="zaloBot.connect.step1"
              components={{
                link: (
                  <a
                    href="https://bot.zaloplatforms.com"
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary underline"
                  />
                ),
                b: <strong />,
              }}
            />
          </li>
          <li>
            <Trans t={t} i18nKey="zaloBot.connect.step2" components={{ b: <strong /> }} />
          </li>
          <li>{t('zaloBot.connect.step3')}</li>
        </ol>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">{t('zaloBot.connect.tokenLabel')}</label>
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
            placeholder={t('zaloBot.connect.tokenPlaceholder')}
            disabled={disabled}
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleConnect} disabled={connect.isPending || disabled}>
            {connect.isPending ? <Loader2 className="animate-spin" /> : <Plug />}
            {t('zaloBot.connect.submit')}
          </Button>
        </div>
      </CardContent>
      {quotaDialog.node}
    </Card>
  )
}

/**
 * Kết quả chẩn đoán, viết theo cách người vận hành đọc được.
 *
 * "Bot kết nối rồi mà tin không về" có ba nguyên nhân rất khác nhau và phải tách
 * ra mới sửa đúng chỗ — nên ở đây kết luận luôn thay vì đổ ra một đống số.
 */
function DiagnosisPanel({ d }: { d: ZaloBotDiagnosis }) {
  const { t } = useTranslation('chanZalo')
  // Thứ tự này có chủ đích: hỏi-không-được phải đứng TRƯỚC mọi kết luận khác.
  // Không nghe được Zalo trả lời thì im lặng nghĩa là "chưa biết", tuyệt đối
  // không được dịch thành "Zalo chưa có webhook" — người dùng sẽ đi sửa nhầm chỗ.
  const ket = d.infoError
    ? {
        variant: 'warning' as const,
        title: t('zaloBot.diag.infoErrorTitle'),
        desc: t('zaloBot.diag.infoErrorDesc', { error: d.infoError }),
      }
    : !d.khop
      ? {
          variant: 'destructive' as const,
          title: d.webhookUrlTaiZalo
            ? t('zaloBot.diag.mismatchOther')
            : t('zaloBot.diag.mismatchNone'),
          desc: t('zaloBot.diag.mismatchDesc'),
        }
      : d.goiThuOk === false
        ? {
            variant: 'destructive' as const,
            title: t('zaloBot.diag.rejectedTitle'),
            desc: d.hint || d.outcome || t('zaloBot.diag.rejectedDesc'),
          }
        : d.soTin > 0
          ? {
              variant: 'success' as const,
              title: t('zaloBot.diag.okTitle', { messages: d.soTin, conversations: d.soHoiThoai }),
              desc: t('zaloBot.diag.okDesc'),
            }
          : {
              variant: 'warning' as const,
              title: t('zaloBot.diag.noMessagesTitle'),
              desc: t('zaloBot.diag.noMessagesDesc'),
            }

  const tone =
    ket.variant === 'destructive'
      ? 'border-destructive/40 bg-destructive/10'
      : ket.variant === 'success'
        ? 'border-success/40 bg-success/10'
        : 'border-warning/40 bg-warning/10'

  return (
    <div className={`mt-2 space-y-2 rounded-md border p-3 text-sm ${tone}`}>
      <p className="font-medium">{ket.title}</p>
      <p className="text-muted-foreground">{ket.desc}</p>
      <dl className="grid gap-x-3 gap-y-1 text-xs text-muted-foreground sm:grid-cols-[auto_1fr]">
        <dt>{t('zaloBot.diag.webhookSystem')}</dt>
        <dd className="break-all font-mono">{d.webhookUrlExpected}</dd>
        <dt>{t('zaloBot.diag.webhookZalo')}</dt>
        <dd className="break-all font-mono">{d.webhookUrlTaiZalo || t('zaloBot.diag.none')}</dd>
        {d.outcome && (
          <>
            <dt>{t('zaloBot.diag.testCall')}</dt>
            <dd>{d.outcome}{d.hint ? ` — ${d.hint}` : ''}</dd>
          </>
        )}
        {d.tinGanNhat && (
          <>
            <dt>{t('zaloBot.diag.lastMessage')}</dt>
            <dd>{dayjs(d.tinGanNhat).format('DD/MM/YYYY HH:mm')}</dd>
          </>
        )}
      </dl>
    </div>
  )
}

/** Danh sách bot đã kết nối. */
function ConnectedBots() {
  const { t } = useTranslation('chanZalo')
  const { data, isLoading, isError } = useZaloBotAccounts()
  const remove = useDeleteZaloAccount()
  const rebind = useRebindZaloBotWebhook()
  const diagnose = useDiagnoseZaloBot()
  const [chanDoan, setChanDoan] = useState<Record<string, ZaloBotDiagnosis>>({})
  // Đăng ký lại webhook = update, ngắt = delete; chẩn đoán chỉ cần quyền xem nên luôn hiện.
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('integrations.create')
  const canUpdate = !!perms?.has('integrations.update')
  const canDelete = !!perms?.has('integrations.delete')

  return (
    <SelectableTable
      rows={data}
      rowKey={(a) => a.id}
      loading={isLoading}
      error={isError}
      filters={ACCOUNT_FILTERS}
      searchText={accountSearchText}
      itemLabel={t('zaloBot.list.itemLabel')}
      empty={
        <EmptyState
          icon={Bot}
          title={t('zaloBot.list.emptyTitle')}
          description={canCreate ? t('zaloBot.list.emptyDesc') : noConnectHint()}
        />
      }
      columns={accountColumns({
        fallbackName: 'Zalo Bot',
        sub: (acc) => acc.externalUid || undefined,
        actions: (acc) => (
          <>
            <Button
              variant="ghost"
              size="sm"
              title={t('zaloBot.list.diagnose')}
              disabled={diagnose.isPending}
              onClick={() =>
                diagnose.mutate(acc.id, {
                  onSuccess: (d) => setChanDoan((s) => ({ ...s, [acc.id]: d })),
                  onError: (e) => toast.error(apiError(e)),
                })
              }
            >
              <Stethoscope />
            </Button>
            {canUpdate && (
              <Button
                variant="ghost"
                size="sm"
                title={t('zaloBot.list.rebind')}
                disabled={rebind.isPending}
                onClick={() =>
                  rebind.mutate(acc.id, {
                    onSuccess: (r) =>
                      r.ok
                        ? toast.success(t('zaloBot.list.rebound'))
                        : toast.warning(r.hint || t('zaloBot.list.rebindFailed')),
                    onError: (e) => toast.error(apiError(e)),
                  })
                }
              >
                <RefreshCw />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                title={t('zaloBot.list.disconnect')}
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(acc.id, {
                    onSuccess: () => toast.success(t('zaloBot.list.disconnected')),
                    onError: (e) => toast.error(apiError(e)),
                  })
                }
              >
                <Unplug />
              </Button>
            )}
          </>
        ),
      })}
      renderDetail={(acc) => (chanDoan[acc.id] ? <DiagnosisPanel d={chanDoan[acc.id]} /> : null)}
      bulkActions={(selected, clear) => <BulkDisconnectButton selected={selected} onDone={clear} />}
    />
  )
}

/** Một tình huống dùng bot — tiêu đề ngắn + một câu nói rõ được lợi gì. */
function UseCase({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="space-y-0.5">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{children}</p>
      </div>
    </div>
  )
}

/**
 * Hướng dẫn "dùng bot để làm gì" — phần khách đọc TRƯỚC khi quan tâm tới token.
 *
 * Khách không mua "một kênh nữa", họ mua việc đỡ tốn người. Nên ở đây nói theo
 * tình huống công việc, và nói thẳng cả những gì bot KHÔNG làm được — giấu đi
 * thì khách phát hiện lúc đã tin dùng, tốn niềm tin hơn nhiều.
 *
 * Tự thu gọn khi đã có bot: người đã đấu nối rồi không cần đọc lại mỗi lần vào.
 */
function HuongDanUngDung({ macDinhMo }: { macDinhMo: boolean }) {
  const { t } = useTranslation('chanZalo')
  const [mo, setMo] = useState<boolean | null>(null)
  const dangMo = mo ?? macDinhMo

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4" />
              {t('zaloBot.guide.title')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              <Trans t={t} i18nKey="zaloBot.guide.intro" components={{ b: <strong /> }} />
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setMo(!dangMo)} className="shrink-0">
            {dangMo ? <ChevronUp /> : <ChevronDown />}
            {dangMo ? t('common:actions.showLess') : t('common:actions.showMore')}
          </Button>
        </div>
      </CardHeader>

      {dangMo && (
        <CardContent className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <UseCase icon={Clock} title={t('zaloBot.guide.uc1Title')}>
              {t('zaloBot.guide.uc1Body')}
            </UseCase>
            <UseCase icon={QrCode} title={t('zaloBot.guide.uc2Title')}>
              {t('zaloBot.guide.uc2Body')}
            </UseCase>
            <UseCase icon={Workflow} title={t('zaloBot.guide.uc3Title')}>
              {t('zaloBot.guide.uc3Body')}
            </UseCase>
            <UseCase icon={FlaskConical} title={t('zaloBot.guide.uc4Title')}>
              {t('zaloBot.guide.uc4Body')}
            </UseCase>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <p className="text-sm font-medium">{t('zaloBot.guide.canTitle')}</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {[1, 2, 3, 4].map((n) => (
                  <li key={n} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                    <span>{t(`zaloBot.guide.can${n}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-1.5">
              <p className="text-sm font-medium">{t('zaloBot.guide.cantTitle')}</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {[1, 2, 3, 4].map((n) => (
                  <li key={n} className="flex gap-2">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span>{t(`zaloBot.guide.cant${n}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Chọn kênh nào là câu hỏi khách hỏi nhiều nhất — trả lời luôn tại đây. */}
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('zaloBot.guide.tblChannel')}</th>
                  <th className="px-3 py-2 font-medium">{t('zaloBot.guide.tblNeed')}</th>
                  <th className="px-3 py-2 font-medium">{t('zaloBot.guide.tblFit')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="px-3 py-2 font-medium">Zalo Bot</td>
                  <td className="px-3 py-2 text-muted-foreground">{t('zaloBot.guide.botNeed')}</td>
                  <td className="px-3 py-2 text-muted-foreground">{t('zaloBot.guide.botFit')}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-medium">Zalo OA</td>
                  <td className="px-3 py-2 text-muted-foreground">{t('zaloBot.guide.oaNeed')}</td>
                  <td className="px-3 py-2 text-muted-foreground">{t('zaloBot.guide.oaFit')}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-medium">{t('zaloBot.guide.personalName')}</td>
                  <td className="px-3 py-2 text-muted-foreground">{t('zaloBot.guide.personalNeed')}</td>
                  <td className="px-3 py-2 text-muted-foreground">{t('zaloBot.guide.personalFit')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  )
}

/**
 * Tab Zalo Bot — kênh Zalo chính thức, đấu nối nhẹ nhất: không OAuth, không quét
 * QR, không chờ xét duyệt. Khách tự làm được trong vài phút.
 */
export function ZaloBotTab() {
  const { t } = useTranslation('chanZalo')
  const { data: cfg, isLoading, isError } = useZaloBotConfigStatus()
  // Dùng chung cache với danh sách bên dưới — không phát sinh lượt gọi thứ hai.
  const { data: bots } = useZaloBotAccounts()
  // Ô dán Bot Token = kết nối kênh mới → cần `integrations.create` (Quản lý / Nhân viên mặc định không có).
  const canCreate = !!useMyPermissions().data?.has('integrations.create')

  if (isLoading) return <Loading label={t('zaloBot.loading')} />
  if (isError || !cfg) return <ErrorState />

  return (
    <div className="space-y-6">
      <HuongDanUngDung macDinhMo={!bots?.length} />

      {!cfg.publicHttps && (
        // Zalo từ chối webhook trỏ localhost/IP nội bộ, nên nói trước thay vì để
        // người dùng kết nối xong mới thấy bot im lặng không nhận tin.
        <div className="flex gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div>
            <p className="font-medium">{t('zaloBot.localhost.title', { apiBase: cfg.apiBase })}</p>
            <p className="text-muted-foreground">
              {t('zaloBot.localhost.desc')}
            </p>
          </div>
        </div>
      )}

      {canCreate && <ConnectForm disabled={!cfg.hasEncryptionKey} />}

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">{t('zaloBot.connectedTitle')}</h3>
        <ConnectedBots />
      </section>
    </div>
  )
}
